

import React, { useEffect, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCIcon, RCCIIconTypes, RCCServicesApi, arrayMove } from "react-common-components";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import hexadecimalToRgb from "src/utils/hexadecimalToRgb";
import { v4 } from "uuid";
import SortableContext from "src/components/Sortable/Context";
import SortableItem from "src/components/Sortable/Item";
import DragOverlay from "src/components/Sortable/DragOverlay";
import { verticalListSortingStrategy } from "src/components/Sortable/Container/strategy";
import SortableContainerWithoutContext from "src/components/Sortable/ContainerWithoutContext";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { ICatba002, ICatbb002 } from "../../interface";
import * as Canvas from "./styles";
import SimplePostIt from "./components/SimplePostIt";
import PostIt from "./components/PostIt";
import ModalsPostIt, { IRefIPostItModals } from "./modals";

interface IModelCanvas{
	canvasModelId?: number;
	canvasSelectedId?: number;
	canvasSelectedCode?: string;
}

const ModelCanvas: React.FC<IModelCanvas> = ({
	canvasModelId,
	canvasSelectedId,
	canvasSelectedCode
}) => {

	const { translate } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();
	const { confirm } = useConfirmModal();

	const refModals = useRef<IRefIPostItModals>(null);

	const defaultPostsIt = [
		{id: "PostIt-1", color: "#ff9ff3"}, 
		{id: "PostIt-2", color: "#feca57"},
		{id: "PostIt-3", color: "#ff6b6b"},
		{id: "PostIt-4", color: "#48dbfb"},
		{id: "PostIt-5", color: "#1dd1a1"},
		{id: "PostIt-6", color: "#54a0ff"} 
	];

	const modelQuadrantsIcons : {code: string, model: string, icon: RCCIIconTypes}[] = [
		{ code: "1", model: "BMC", icon: "houseKeys"},
		{ code: "2", model: "BMC", icon: "key"},
		{ code: "3", model: "BMC", icon: "puzzlePiece"},
		{ code: "4", model: "BMC", icon: "gift"},
		{ code: "5", model: "BMC", icon: "heart"},
		{ code: "6", model: "BMC", icon: "truck"},
		{ code: "7", model: "BMC", icon: "pieChart"},
		{ code: "8", model: "BMC", icon: "money"},
		{ code: "9", model: "BMC", icon: "cashRegister"},
		{ code: "01", model: "BMY", icon: "handsHelp"},
		{ code: "02", model: "BMY", icon: "workshop"},
		{ code: "03", model: "BMY", icon: "personFill"},
		{ code: "04", model: "BMY", icon: "upgrade"},
		{ code: "05", model: "BMY", icon: "teamIdea"},
		{ code: "06", model: "BMY", icon: "exchange"},
		{ code: "07", model: "BMY", icon: "handsHelp"},
		{ code: "08", model: "BMY", icon: "deliver"},
		{ code: "09", model: "BMY", icon: "receiveMoney"},
		{ code: "1", model: "LMC", icon: "exclamationTriangle"},
		{ code: "2", model: "LMC", icon: "solution"},
		{ code: "3", model: "LMC", icon: "chartBar"},
		{ code: "4", model: "LMC", icon: "gift"},
		{ code: "5", model: "LMC", icon: "run"},
		{ code: "6", model: "LMC", icon: "truck"},
		{ code: "7", model: "LMC", icon: "users"},
		{ code: "8", model: "LMC", icon: "money"},
		{ code: "9", model: "LMC", icon: "cashRegister"}
	];

	const [activeId, setActiveId] = useState<string | null>(null);
	const [waitingForResponse, setWaitingForResponse] = useState<boolean>(false);
	const [dictionaryColumns, setDictionaryColumns] = useState<Record<string, ICatba002>>({});
	const [dataColumns, setDataColumns] = useState<Record<string, ICatbb002[]>>({});

	const [tempDataColumns, setTempDataColumns] = useState<Record<string, ICatbb002[]>>({});

	const [containers, setContainers] = useState<string[] | null>(Object.keys(dictionaryColumns) ?? null);

	const queryCanvasQuadrants = async (id: number) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Canvas/SelectQuadrants",
				urlParameters: {
					id,
					canvasId: canvasSelectedId
				}
			});
      
			setDictionaryColumns(data.dictionaryColumns);
			setDataColumns(data.dataColumns);
			setContainers(Object.keys(data.dictionaryColumns));

			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}		
	};

	const findTypeCard = (nameCard: string) => {
		const name = nameCard.split("-");
		if(name[0] === "PostIt")
			return ["PostIt", name[1]];
		if(name[0] === "Line")
			return ["Line", name[1]];
		if(name[0] === "Quadrant")
			return ["Quadrant", name[1]];
		return [null, null];
	};

	const findColorPostIt = (id: string) => {
		const nameObj = id.includes("PostIt") ? id : `PostIt-${id}`;
		const obj = defaultPostsIt.find((item) => item.id === nameObj);
		if(obj)
			return obj.color;
		return "#fff";
	};

	const findContainer = (idX: string) => {
		if (idX in dataColumns) {
			return idX;
		}
		return Object.keys(dataColumns).find((key) =>	dataColumns[key].map(({ id }) => id).find((key2) => key2.includes(idX)));
	};

	const addCard = async (postIt: string, quadrantId: string, items?: ICatbb002[]  ) => {
		setWaitingForResponse(true);
		try{

			const ids = items ? items.map(({ cab002Id }) => cab002Id) : undefined;

			const response = await RCCServicesApi.postRequest({
				method: "Canvas/InsertPostIt",
				body: { 
					canvasId: canvasSelectedId,
					quadrantId,
					color: findColorPostIt(postIt),
					description: "",
					ids
				}
			});

			const data = response.data as ICatbb002;
			const newItem = {
				...data,
				id: `Line-${data.cab002Id}`
			};

			if(items){
				const tempItems = items.map((item) => {
					if(item.id === "0")
						return newItem;
					return item;
				});	
				setDataColumns((itemsX) => ({
					...itemsX,
					[`Quadrant-${quadrantId}`]: tempItems
				}));
			}else{
				setDataColumns((itemsX) => ({
					...itemsX,
					[`Quadrant-${quadrantId}`]: [
						...itemsX[`Quadrant-${quadrantId}`],
						newItem
					]
				}));
			}

			addToast({
				color: "green",
				description: translate("Post-it adicionado com sucesso"),
				title: translate("Sucesso")
			});

			setWaitingForResponse(false);

		}catch(err){
			setDataColumns(tempDataColumns);
			standardErrorMessages(err, translate("Erro ao adicionar card no dashboard"), addToast);
			setWaitingForResponse(false);
		}

	};

	const queryOrderRows = async (rows: ICatbb002[] | any[], column: string) => {
		setWaitingForResponse(true);


		const rowsOrdered: any[] = [];

		rows.forEach((item: ICatbb002, index) => {
			const row = {
				Cab002Id: item.cab002Id,
				Cab002Cab001Id: canvasSelectedId,
				Cab002Caa002Id: parseInt(column, 10),
				Cab002Ordem: index + 1
			};
			rowsOrdered.push(row);
		});

		try{
			await RCCServicesApi.putRequest({
				method: "Canvas/UpdateOrderPostIts",
				body: {
					rows: rowsOrdered
				}
			});
			setWaitingForResponse(false);

			addToast({
				color: "green",
				description: translate("Post-it movido com sucesso"),
				title: translate("Sucesso")
			});

			return true;
		}catch(err){
			standardErrorMessages(err, translate("Erro ao mover Post-It"), addToast);
			setWaitingForResponse(false);
			return false;
		}

	};

	const DragOverlayRender = (idActive: string) => {
		if(idActive){
			
			const cardActive = findTypeCard(idActive);

			const typeCardActive = cardActive[0];

			if(typeCardActive === "PostIt"){
				const newPostItemItem  = defaultPostsIt.find((e) => e.id === idActive);

				const itemToRender = {
					cab002Descricao: "",
					cab002Cor: newPostItemItem?.color
				} as ICatbb002;

				return renderSortableItemDragOverlay(itemToRender, true);
			}

			const container = findContainer(idActive);
			let item;

			if(container && dataColumns)
				item = dataColumns[container].find((itemX) => itemX.id === idActive);

			return renderSortableItemDragOverlay(item);
		}
		return null;
	};

	const renderSortableItemDragOverlay = (row: ICatbb002 | undefined, placeholder?: boolean) => (
		<PostIt 
			key={row?.id}
			value={row || {} as ICatbb002}
			placeholder={placeholder}
		/>
	);

	const deletePostIt = async (idX: string) => {
		if(!idX || idX === null)
			return;

		async function deleteMethod(){
			const id = idX.replace("Line-", "");
			await RCCServicesApi.deleteRequest({
				method: "Canvas/DeletePostIt",
				urlParameters: {
					id
				}
			});
		}

		confirm(
			translate("Exclusão de Post-It"), 
			`${translate("Deseja realmente excluir este Post-it")}?`,
			async () => {

				await deleteMethod();

				addToast({
					color: "green",
					description: translate("Post-It excluído com sucesso"),
					title: translate("Sucesso")
				});

				const container = findContainer(idX);
				if(!container)
					return;
				setDataColumns((itemsX) => ({
					...itemsX,
					[container]: [
						...itemsX[container].filter((item) => item.id !== idX)
					]
				}));
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir Post-It"), addToast);
			},
			true
		);	
	};

	const callbackUpdatePostIt = (id: number, description: string) => {

		const container = findContainer(`Line-${id}`);
		if(!container)
			return;

		const item = dataColumns[container].find((itemY) => itemY.id === `Line-${id}`);
		if(!item)
			return;

		const itemModified = {
			...item,
			cab002Descricao: description
		} as ICatbb002;

		const newData = dataColumns[container].map((element) => {
			if(element.id === `Line-${id}`)
				return itemModified;
			return element;
		});

		setDataColumns((itemsX) => ({
			...itemsX,
			[container]: [
				...newData
			] 
		}));

	};

	const selectIconBasedOnTitle = (code: string, typeCanvas?: string) => {
		if(!code || !typeCanvas)
			return "infoCircle" as RCCIIconTypes;
		const item = modelQuadrantsIcons.find((itemX) => itemX.code === code && typeCanvas === canvasSelectedCode);
		if(!item)
			return "infoCircle" as RCCIIconTypes;
		return item.icon;
	};

	useEffect(() => {
		setContainers(null);
		setDictionaryColumns({});
		setDataColumns({});

		if(canvasModelId)
			queryCanvasQuadrants(canvasModelId);
		else{
			setContainers(null);
			setDictionaryColumns({});
			setDataColumns({});
		}
	}, [canvasSelectedId]);

	return (
		<>
			<div>

				<SortableContext 
					executeOnDragEndWhenActiveAndOverEquals
					onDragStart={(activeIdX: string) => {
						setActiveId(activeIdX);
						setTempDataColumns(dataColumns);
					}}
					onDragOver={(activeIdX: string, overId: string) => {
						if (!overId || activeIdX in dataColumns) {
							return;
						}

						const activeContainer = findContainer(activeIdX);
						const overContainer = findContainer(overId);

						if (!overContainer || !activeContainer) {
							return;
						}
						
						if (activeContainer !== overContainer) {
							setDataColumns((itemsX) => {
								const activeItems = itemsX[activeContainer];
								const overItems = itemsX[overContainer];
								const overIndex = overItems.map(({ id }) => id).indexOf(overId);
								const activeIndex = activeItems.map(({ id }) => id).indexOf(activeIdX);

								let newIndex: number;

								if (overId in dataColumns) {
				 					newIndex = overItems.length + 1;
								}else{
									newIndex = overIndex >= 0 ? overIndex : overItems.length + 1;
								}

								return {
									...itemsX,
									[activeContainer]: itemsX[activeContainer].filter(
										(item) => String(item.id) !== activeIdX
									),
									[overContainer]: [
										...itemsX[overContainer].slice(0, newIndex),
										itemsX[activeContainer][activeIndex],
										...itemsX[overContainer].slice(
											newIndex,
											itemsX[overContainer].length
										)
									]
								};

							});
						}

					}}
					onDragEnd={async (activeIdX: string, overId: string) => {
						const [typeCardActive, idCardActive] = findTypeCard(activeIdX);
						const [typeCardOver, idCardOver] = findTypeCard(overId);

						if(typeCardActive === "PostIt"){
							if(!idCardActive || !idCardOver)
								return;

							if(typeCardOver === "Quadrant"){
								addCard(idCardActive, idCardOver);	
								return;
							}

							if(typeCardOver === "Line"){
								const containerOver = findContainer(idCardOver);	

								if(!containerOver)
									return;

								const cardOver = findTypeCard(containerOver);

								const containerOverId = cardOver[1];

								const fakeCard = {
									id: "0",
									cab002Id: 0, 
									cab002Descricao: " ", 
									cab002Cor: findColorPostIt(idCardActive) 
								} as ICatbb002;

								let temp = [
									...dataColumns[containerOver],
									fakeCard
								];	

								const activeIndex = temp.findIndex((field) => field.id === "0");
								const overIndex = temp.findIndex((field) => field.id === overId);
						
								temp = arrayMove(temp, 
									activeIndex, 
									overIndex
								);

								addCard(idCardActive, containerOverId ?? "0", temp);
							}


						}else{
						
							const containerActive = findContainer(activeIdX);	

							if (!containerActive) {
								setActiveId(null);
								return;
							}
					
							if (!overId) {
								setActiveId(null);
								return;
							}

							const containerOver = findContainer(overId);	

							if(!containerOver)
								return;

							const cardOver = findTypeCard(containerOver);

							const containerOverId = cardOver[1];

							if (containerOver) {
								const activeIndex = dataColumns[containerActive].map(({ id }) => id).indexOf(activeIdX);
								const overIndex = dataColumns[containerActive].map(({ id }) => id).indexOf(overId);

								const ordenedData = arrayMove(dataColumns[containerOver], activeIndex, overIndex);
					
								const originalItems = dataColumns;
					
								setDataColumns((itemsX) => ({
									...itemsX,
									[containerOver]: arrayMove(
										itemsX[containerOver],
										activeIndex,
										overIndex
									)
								}));
								const res = await queryOrderRows(ordenedData, containerOverId ?? "0");
								if(!res){
									setDataColumns(originalItems);
								}
							}
							setActiveId(null);
							
						}
					}}
				>

					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCGrid container item>
							{canvasSelectedId && (
								<>
									{defaultPostsIt.map((item) => (
										<SortableItem 
											id={item.id} 
											styles={{ width: "" }}
											key={v4()}
										>
											<SimplePostIt color={item.color} />
										</SortableItem>
									))}
								</>
							)}	
						</RCCGrid>
										
					</CheckPermission>
					
					<Canvas.Container>
						{canvasSelectedId && dictionaryColumns && dataColumns && containers && containers.map((quadrant, index) => (
							<React.Fragment key={v4()}>	
								<Canvas.Quadrant 
									area={`area${index + 1}`}
									height={[1, 4, 7].includes(index + 1) ? "500px": "250px"}
									background={hexadecimalToRgb(dictionaryColumns[quadrant].caa002Cor)}
								>
									<Canvas.Header
										colorItems={dictionaryColumns[quadrant].caa002Cor}
									>
										<Canvas.HeaderDivisor>
											<RCCIcon 
												name={selectIconBasedOnTitle(dictionaryColumns[quadrant].caa002Codigo, canvasSelectedCode)}
											/>
											<span>|</span>
											<span>{translate(dictionaryColumns[quadrant].caa002Descricao)}</span>
										</Canvas.HeaderDivisor>
										<Canvas.HeaderDivisor>
											<RCCIcon 
												cursor="pointer"
												name="infoCircle"
												onClick={() => refModals.current?.actionButtonVisualizeExplanation(dictionaryColumns[quadrant])}
											/>
										</Canvas.HeaderDivisor>
									</Canvas.Header>
									<Canvas.Content>
										<SortableItem
											id={`Quadrant-${dictionaryColumns[quadrant].caa002Id.toString()}`}
											styles={{ height: "100%" }}
										>
											<SortableContainerWithoutContext
												strategy={verticalListSortingStrategy}
												items={dataColumns[quadrant] ? dataColumns[quadrant].map((line) => ({
													...line,
													id: `Line-${line.cab002Id.toString()}`
												})) : []}
											>										
												<div style={{ height: "100%" }}>
													{dataColumns[quadrant].map((line) => (
														<SortableItem 
															disabled={waitingForResponse} 
															id={`Line-${line.cab002Id.toString()}`} 
															key={`line-dashboard-${line.cab002Id.toString()}`}
														>	
															<PostIt 
																value={line} 
																deletePostIt={deletePostIt}
																callbackUpdatePostIt={callbackUpdatePostIt}
															/>
														</SortableItem>
													))}
												</div>
											</SortableContainerWithoutContext>
										</SortableItem>
									</Canvas.Content>
								</Canvas.Quadrant>	
							</React.Fragment>
						))}
					</Canvas.Container>
			
					<DragOverlay>
						{activeId ? DragOverlayRender(activeId) : null}
					</DragOverlay>

				</SortableContext>
			</div>

			<ModalsPostIt
				ref={refModals}
			/>

		</>
	);

};

export default ModelCanvas;
